import { useCallback } from 'react';
import { commitMutation, useRelayEnvironment } from 'react-relay';
import * as Sentry from '@sentry/react';
import { setClassCategory } from '../graphQLOperations/ClassCategories';
import { ClassCategoriesSetClassCategoryMutation } from '../graphQLOperations/__generated__/ClassCategoriesSetClassCategoryMutation.graphql';
import { useAppDispatch } from '../redux/hooks';
import { setClassIconOverride } from '../redux/reducers/classIconsSlice';

/**
 * Saves a class icon and reflects it everywhere straight away.
 *
 * Committed against the environment rather than through useMutation: the dialogs
 * that save an icon close in the same tick, and useMutation cancels whatever is
 * still in flight when its component unmounts.
 */
export const useSaveClassIcon = (): ((classId: string | null | undefined, icon: string | null) => void) => {
  const environment = useRelayEnvironment();
  const dispatch = useAppDispatch();

  return useCallback(
    (classId: string | null | undefined, icon: string | null) => {
      if (!classId || !icon) return;

      dispatch(setClassIconOverride({ classId, icon }));

      commitMutation<ClassCategoriesSetClassCategoryMutation>(environment, {
        mutation: setClassCategory,
        variables: { input: { classId, category: 'ICON', value: icon } },
        onCompleted: response => {
          const message = response?.setClassCategory?.errors?.[0]?.message;
          if (message) Sentry.captureMessage(`Could not save the class icon: ${message}`, 'error');
        },
        onError: error => Sentry.captureException(error),
      });
    },
    [environment, dispatch],
  );
};
